import React, { FC } from "react";

import { KpkApiPublicShareStatus } from "@keepeek/api-client";
import { useBasketShare, useCurrentUser } from "@keepeek/commons";
import { KButton } from "@keepeek/refront-components";
import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";

import { CommonBasketShareProps } from "./index";

const PREFIX = "PublicShareActivate";

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  button: `${PREFIX}-button`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: "absolute",
    background: "rgba(255,255,255,1)",
    top: 0,
    left: theme.spacing(-1),
    bottom: theme.spacing(-1),
    right: theme.spacing(-1),
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  [`& .${classes.content}`]: {
    textAlign: "center",
    display: "block",
    width: "100%",
  },

  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
  },

  [`& .${classes.button}`]: {
    padding: theme.spacing(2),
    minWidth: "50%",
  },
}));

const PublicShareActivate: FC<CommonBasketShareProps> = function ({
  basketId,
  basketTitle,
  sentryCallback,
  snackbarCallback,
}) {
  const { t } = useTranslation();
  const { displayBackofficeSharedBasket } = useRecoilValue(
    configSectionComponentBasketSelector,
  ) ?? {
    displayBackofficeSharedBasket: undefined,
  };
  const { selectedStatus, publish, publicShareLoading } = useBasketShare(
    displayBackofficeSharedBasket ?? false,
    basketId,
    sentryCallback,
    snackbarCallback,
  );

  const { hasAttachmentsRights } = useCurrentUser();

  if (selectedStatus === KpkApiPublicShareStatus.Deactivated) {
    return (
      <StyledBox className={classes.root}>
        <Box className={classes.content}>
          {publicShareLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Typography variant="subtitle1" component="div" className={classes.title}>
                {t("basket.share.content.activate.title", { basketTitle: basketTitle ?? "" })}
              </Typography>
              <KButton
                startIcon={<KIcon className={"fa-solid fa-circle-check"} />}
                variant="contained"
                size="large"
                className={classes.button}
                onClick={() =>
                  publish({
                    closePopup: false,
                    initialAttachmentsVisible: hasAttachmentsRights,
                  })
                }
                testId="publicShareActivateButton"
              >
                {t("basket.share.content.activate.button")}
              </KButton>
            </>
          )}
        </Box>
      </StyledBox>
    );
  }
  return null;
};

export default PublicShareActivate;
